// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	WhatsAppChat,
	WhatsAppMessage,
	WhatsAppPresence,
	WhatsAppStatus,
} from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';

export type WhatsAppChatFilter = 'all' | 'unread' | 'groups' | 'archived';

export function filterWhatsAppChats(chats: Array<WhatsAppChat>, query: string, filter: WhatsAppChatFilter) {
	const needle = query.trim().toLowerCase();
	return chats.filter(
		(chat) =>
			(filter === 'archived' ? chat.archived : !chat.archived) &&
			(filter !== 'unread' || chat.unread > 0) &&
			(filter !== 'groups' || chat.is_group) &&
			(!needle || chat.name.toLowerCase().includes(needle) || (chat.last_preview ?? '').toLowerCase().includes(needle)),
	);
}

/** Group pictures identify the chat; bubbles must identify the individual sender. */
export function whatsAppMessageAvatar(
	message: WhatsAppMessage,
	chat: WhatsAppChat | undefined,
	status: WhatsAppStatus | null,
) {
	if (message.from_me) {
		const phone = status?.phone?.replace(/\D/g, '');
		return {name: status?.display_name ?? 'You', jid: phone ? `${phone}@s.whatsapp.net` : '', hasPicture: !!phone};
	}
	if (chat?.is_group) {
		const jid = message.sender_avatar_jid ?? message.sender_jid ?? '';
		return {name: message.sender_name ?? 'Unknown contact', jid, hasPicture: !!jid && message.sender_has_picture};
	}
	return {
		name: chat?.name ?? message.sender_name ?? 'Unknown contact',
		jid: chat?.jid ?? message.jid,
		hasPicture: chat?.has_picture ?? message.sender_has_picture,
	};
}

/** Pinned chats first, each group in the list's own order. */
export function splitPinnedChats(chats: Array<WhatsAppChat>): {pinned: Array<WhatsAppChat>; rest: Array<WhatsAppChat>} {
	return {pinned: chats.filter((chat) => chat.pinned), rest: chats.filter((chat) => !chat.pinned)};
}

/**
 * The line under the chat name, as WhatsApp Web shows it: typing beats
 * online beats last seen; groups only ever say who is typing. Returns null
 * when there is nothing live to say, so the caller shows its static text.
 */
export function whatsAppPresenceLine(
	presence: WhatsAppPresence | undefined,
	chat: Pick<WhatsAppChat, 'is_group'> | undefined,
	now = Date.now(),
): string | null {
	if (!presence) return null;
	const group = chat?.is_group === true;
	if (presence.state === 'composing' || presence.state === 'recording') {
		const verb = presence.state === 'recording' ? 'recording audio…' : 'typing…';
		if (!group) return verb;
		const who = presence.participant_name ?? (presence.participant ? `+${presence.participant.split('@')[0]}` : null);
		return who ? `${who} is ${verb}` : verb;
	}
	if (group) return null;
	if (presence.state === 'available') return 'online';
	if (presence.last_seen) return `last seen ${lastSeenLabel(presence.last_seen * 1000, now)}`;
	return null;
}

function lastSeenLabel(atMs: number, now: number): string {
	const at = new Date(atMs);
	const time = at.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
	const today = new Date(now);
	today.setHours(0, 0, 0, 0);
	const day = new Date(atMs);
	day.setHours(0, 0, 0, 0);
	const diff = Math.round((today.getTime() - day.getTime()) / 86400000);
	if (diff === 0) return `today at ${time}`;
	if (diff === 1) return `yesterday at ${time}`;
	return `${at.toLocaleDateString(undefined, {day: 'numeric', month: 'short'})} at ${time}`;
}

/** How far a swiped bubble follows the finger: free up to the trigger point, then it resists. */
export function swipeOffset(dx: number, trigger = 56, max = 88): number {
	const magnitude = Math.abs(dx);
	const eased = magnitude <= trigger ? magnitude : trigger + (magnitude - trigger) * 0.35;
	return Math.sign(dx) * Math.min(max, eased);
}

/**
 * Avatar initials: first and last word, ignoring a trailing note such as
 * "(You)" and anything that is not a letter or digit ("JP de la Rey (You)" -> "JR").
 */
export function whatsAppInitials(name: string): string {
	const words = name
		.replace(/\s*\([^()]*\)\s*$/, '')
		.split(/\s+/)
		.map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
		.filter(Boolean);
	if (words.length === 0) return '?';
	if (words.length === 1) return Array.from(words[0]).slice(0, 2).join('').toUpperCase();
	return (Array.from(words[0])[0] + Array.from(words[words.length - 1])[0]).toUpperCase();
}

/** WhatsApp accepts an edit for 15 minutes after the message was sent. */
export const WHATSAPP_EDIT_WINDOW_MS = 15 * 60 * 1000;
const CAPTIONED_KINDS = new Set(['image', 'video', 'document']);

/**
 * Whether the Edit action applies: your own delivered text message, or a
 * photo, video or document caption, inside WhatsApp's 15-minute window.
 */
export function canEditWhatsAppMessage(
	message: Pick<WhatsAppMessage, 'from_me' | 'local' | 'media_kind' | 'sent_at' | 'status' | 'text'>,
	now: number = Date.now(),
): boolean {
	if (!message.from_me || message.local || message.status === 'failed') return false;
	if (message.media_kind != null && !(CAPTIONED_KINDS.has(message.media_kind) && message.text)) return false;
	if (message.media_kind == null && !message.text) return false;
	const sent = Date.parse(message.sent_at);
	return Number.isFinite(sent) && now - sent <= WHATSAPP_EDIT_WINDOW_MS;
}

/** Pictures sent this close together by one person show as one album, as WhatsApp does. */
export const WHATSAPP_ALBUM_GAP_MS = 2 * 60_000;

/** A photo or clip that can sit in an album: no caption, no reply quote, not a failed send. */
export function isWhatsAppAlbumItem(message: WhatsAppMessage): boolean {
	return (
		(message.media_kind === 'image' || message.media_kind === 'video') &&
		// A GIF loops on its own, so it keeps its own bubble as on WhatsApp.
		!message.media_gif &&
		!message.text &&
		!message.quote_text &&
		message.status !== 'failed' &&
		!message.poll &&
		!message.location &&
		!message.contacts?.length
	);
}

/**
 * The conversation in display blocks: each block is one message's index, or the indexes of two or
 * more photos or clips in a row from one sender on one day, each within two minutes of the one before (an
 * album). `breakAt` (the first unread message) always starts a new block, so the divider stays put.
 */
export function whatsAppAlbumRuns(
	messages: ReadonlyArray<WhatsAppMessage>,
	breakAt: string | null = null,
): Array<Array<number>> {
	const blocks: Array<Array<number>> = [];
	const senderOf = (message: WhatsAppMessage) => (message.from_me ? 'me' : (message.sender_jid ?? message.jid));
	messages.forEach((message, index) => {
		const block = blocks[blocks.length - 1];
		const previous = index > 0 ? messages[index - 1] : null;
		const joins =
			block !== undefined &&
			previous !== null &&
			message.id !== breakAt &&
			isWhatsAppAlbumItem(message) &&
			isWhatsAppAlbumItem(previous) &&
			senderOf(message) === senderOf(previous) &&
			new Date(message.sent_at).toDateString() === new Date(previous.sent_at).toDateString() &&
			Math.abs(Date.parse(message.sent_at) - Date.parse(previous.sent_at)) <= WHATSAPP_ALBUM_GAP_MS;
		if (joins) block.push(index);
		else blocks.push([index]);
	});
	return blocks;
}
